import React, { Component, Fragment } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { Responsive, WidthProvider } from 'react-grid-layout'
import { observer } from 'mobx-react'
import ReactResizeDetector from 'react-resize-detector'
import * as R from 'ramda'
import queryString from 'query-string'
import i18n from 'i18next'
import DynamicDashboardStore from './DynamicDashboardStore'
import DashboardHeader from './DashboardHeader'
import DashboardWizardStore from './creationWizard/DashboardWizardStore'
import CreationWizard from './creationWizard'
import WidgetContainer from './WidgetContainer'
import NoDashboardsView from './NoDashboardsView'
import AppLoading from '../AppLoading'
import TemplateCloneModal from './TemplateCloneModal'
import ManageDashboardsModal from './ManageDashboardsModal'
import Button from '../../UniformComponents/Button'
import LevelSelector from './LevelSelector'

import './dynamicDashboard.scss'
import '../../../node_modules/react-grid-layout/css/styles.css'
import DashboardClone from './modal/DashboardClone'
import DashboardRename from './modal/DashboardRename'
import DashboardDelete from './modal/DashboardDelete'
import DashboardExport from './modal/DashboardExport'
import ConfirmationModal from '../../UniformComponents/ConfirmationModal'
import ComparisonModal from './ComparisonModal'
import LockSlider from './LockSlider'
import WidgetType from './creationWizard/WidgetType'
import WidgetCreator from './creationWizard/WidgetCreator'

import FilterModal from '../ReportV2/components/Modal/Filter'
import FilterRespondentTypeModal from './FilterRespondentTypeModal'
import Select from '../form/Select'

const ResponsiveGridLayout = WidthProvider(Responsive)
@observer
class DynamicDashboard extends Component {
    constructor(props) {
        super(props)
        this.state = {
            initializing: true,
            menuOpen: false,
            showComparisonModal: false,
        }
        this.init()
    }

    componentDidMount() {
        window.addEventListener('beforeunload', this.handleBeforeUnload)
    }

    componentWillUnmount() {
        window.removeEventListener('beforeunload', this.handleBeforeUnload)
    }

    init = async () => {
        const { store } = this.props
        await this.setInitialRoute()
        this.DynamicDashboardStore = new DynamicDashboardStore(store)
        this.DynamicDashboardStore.init()
        this.dashboardWizardStore = new DashboardWizardStore(
            store,
            this.DynamicDashboardStore,
        )
        this.dashboardWizardStore.init()
        this.setState({ initializing: false })
    }

    handleBeforeUnload = (event) => {
        const { editing } = this.DynamicDashboardStore
        if (editing) {
            const message = 'You have unsaved changes. Are you sure you want to leave?'
            event.returnValue = message
            return message
        }
    };

    setInitialRoute = async () => {
        const { location } = window
        const { store, history } = this.props
        const { userData } = store
        const { role } = userData
        const params = queryString.parse(location.search)

        if ([100, 110, 150].includes(role)) return

        const [divisions, portfolios, products] = await Promise.all(
            [
                this.getDivisions(),
                this.getPortfolios(),
                this.getProducts(),
            ],
        )

        const divisionIds = divisions.map((d) => String(d.divisionId))
        const portfolioIds = portfolios.map((p) => String(p.portfolioId))
        const productIds = products.map((p) => String(p.productId))

        const recordId = params.productId || params.portfolioId || params.divisionId
        if (!recordId) {
            if (role === 120) {
                history.push(`?divisionId=${divisions[0].divisionId}`)
            } else if (role === 130) {
                history.push(`?portfolioId=${portfolios[0].portfolioId}`)
            } else if ([140, 170].includes(role)) {
                history.push(`?productId=${products[0].productId}`)
            }
        } else if (params.productId && !productIds.includes(params.productId)) {
            history.push(`?productId=${products[0].productId}`)
        } else if (params.portfolioId && !portfolioIds.includes(params.portfolioId)) {
            history.push(`?portfolioId=${portfolios[0].portfolioId}`)
        } else if (params.divisionId && !divisionIds.includes(params.divisionId)) {
            history.push(`?divisionId=${divisions[0].divisionId}`)
        }
    }

    getDivisions = async () => {
        const { store } = this.props
        const { userData } = store
        const { role } = userData
        if (role > 120 && role !== 150) return []
        try {
            return await store.http.get('/division')
        } catch (e) {
            return []
        }
    }

    getPortfolios = async () => {
        const { store } = this.props
        const { userData } = store
        const { role } = userData
        if (role > 130 && role !== 150) return []
        try {
            return await store.http.get('/portfolio')
        } catch (e) {
            return []
        }
    }

    getProducts = async () => {
        const { store } = this.props
        try {
            return await store.http.get('/product')
        } catch (e) {
            return []
        }
    }

    renderElements = () => {
        const {
            widgets, getChart, removeChart, years, benchmarks, benchmarkPeriod,
        } = this.DynamicDashboardStore
        const chartProps = {
            hideMetadata: true,
            style: {
                marginBottom: 0,
                paddingBottom: 0,
                height: '100%',
                width: '100%',
            },
        }
        // reading the years here triggers mobx to rerender correctly.
        // won't rerender when read in the getChart function only
        const yearFilters = Array.from(years.keys()).filter((y) => !!years.get(y))

        const benchmarkFilters = Array.from(benchmarks.keys()).filter((y) => !!benchmarks.get(y))

        const elements = widgets.map((el) => {
            chartProps.chartId = `elChartId ${el.key}`
            chartProps.title = this.getChartTitle(el)
            chartProps.kpi = el.kpiType
            const scrollable = R.pathOr(false, ['widgetRenderSettings', 'scrollable'], el)
            return (
                <div
                    key={`${el.key}`}
                    style={{ width: '100%' }}
                >
                    <ReactResizeDetector>
                        {({ width, height }) => (
                            <WidgetContainer
                                chart={getChart(
                                    { ...el, containerSize: { width, height } },
                                    el.data,
                                    yearFilters, benchmarkFilters, benchmarkPeriod,
                                )}
                                title={el.title}
                                renderChart={width}
                                containterSize={{ width, height }}
                                removeChart={removeChart}
                                chartKey={el.key}
                                DynamicDashboardStore={this.DynamicDashboardStore}
                                scrollable={scrollable}
                                chartType={el.chartType}
                                widget={el}
                            />
                        )}
                    </ReactResizeDetector>
                </div>

            )
        })
        return elements
    }

    renderLevelHeaderButtons =() => {
        const { toggleManageDashboardsModal, toggleShowCreateDashboard, appStore } = this.DynamicDashboardStore
        const { userData } = appStore
        const { role } = userData
        if ([170, 150].includes(role)) return null
        return (
            <div className="headerLevelButtons">
                <Button
                    label={i18n.t('dashboards.manageDashboards')}
                    icon="fa-light fa-sliders"
                    cssClass="large tertiary"
                    onClick={toggleManageDashboardsModal}
                />
                <Button
                    label={i18n.t('dashboards.createDashboards')}
                    icon="fa-regular fa-plus"
                    cssClass="large tertiary"
                    onClick={toggleShowCreateDashboard}
                />
            </div>
        )
    }

    renderLevelSelect = () => {
        const {
            changeLevel, getLevels, level, recordId,
        } = this.DynamicDashboardStore
        const levels = getLevels()

        return (
            <LevelSelector
                levels={Array.from(levels).map(([l, dashboard]) => ({ level: l, dashboard }))}
                currentLevel={level}
                changeLevel={changeLevel}
                key={`levelSelector_${recordId}`}
            />
        )
    }

    toggleComparisonModal = () => {
        const { showComparisonModal } = this.state
        this.setState({ showComparisonModal: !showComparisonModal })
    }

    toggleMenuOpen = (e) => {
        e.stopPropagation()
        const { menuOpen } = this.state

        if (!menuOpen) document.body.addEventListener('click', this.toggleMenuOpen)
        if (menuOpen) document.body.removeEventListener('click', this.toggleMenuOpen)

        this.setState({ menuOpen: !menuOpen })
    }

    renderMenu = () => {
        const { menuOpen } = this.state
        return (
            <div
                className="menu"
            >
                <div
                    onClick={this.toggleMenuOpen}
                    className="menu-icon"
                    style={{
                        backgroundColor: menuOpen ? '#EDF1F7' : undefined,
                    }}
                >
                    <i className="fa-regular fa-ellipsis-vertical" />
                </div>
                <div
                    className="menu-container"
                    style={{
                        display: menuOpen ? 'block' : 'none',
                    }}
                >
                    <ul style={{
                        listStyleType: 'none', fontSize: '13px', cursor: 'pointer', height: '100%', position: 'relative', padding: '0',
                    }}
                    >
                        {this.DynamicDashboardStore.getMenuOptions()}
                    </ul>
                </div>
            </div>
        )
    }

    pinnedFilter = () => {
        const {
            filterStore, editPinnedFilter, getPinnedFilterValue,
        } = this.DynamicDashboardStore
        const { pinnedFilterOptions, pinnedFilterQuestion } = filterStore
        const filterValue = getPinnedFilterValue()
        if (!pinnedFilterQuestion) return null

        return (
            <div className="pinnedFilter" style={{ width: '250px' }}>
                <Select
                    defaultValue={filterValue}
                    options={pinnedFilterOptions.map((o) => ({ ...o, value: o.id }))}
                    label={i18n.t('report.filter.quick.enable')}
                    onBlur={editPinnedFilter}
                    isMulti
                    blurOnClear
                    showNumberSelected
                    key={pinnedFilterQuestion}
                />
            </div>
        )
    }

    renderDashboardMenuOptions = () => {
        const {
            editing,
            beginSaveDashboard,
            editDashboard,
            dashboard,
            appStore,
            loading,
        } = this.DynamicDashboardStore
        const { toggleShow } = this.dashboardWizardStore

        const { userData } = appStore

        const canEdit = ![150, 170].includes(userData.role) && !loading

        return (
            <Fragment>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '16px',
                }}
                >
                    {dashboard && R.pathOr(undefined, ['value'], dashboard) !== 'new' && canEdit
                        ? (
                            <Fragment>
                                <b>{i18n.t('dashboards.editDashboard')}</b>
                                <LockSlider
                                    save={beginSaveDashboard}
                                    edit={editDashboard}
                                    editing={editing}
                                />
                            </Fragment>
                        )
                        : null}
                    {' '}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    {dashboard && R.pathOr(undefined, ['value'], dashboard) !== 'new' && canEdit ? (
                        <Fragment>
                            <Button
                                cssClass="large tertiary"
                                onClick={this.toggleComparisonModal}
                                icon="fa-light fa-filter-list"
                                label={i18n.t('dashboards.addComparison')}
                            />
                            {editing
                                ? (
                                    <Button
                                        cssClass="large tertiary"
                                        onClick={toggleShow}
                                        icon="fa-light fa-chart-pie-simple"
                                        label={i18n.t('dashboards.createWidget')}
                                    />
                                ) : null }
                            {process.env.NEW_WIDGET_CREATOR
                                ? (
                                    <Button
                                        cssClass="large tertiary"
                                        onClick={this.dashboardWizardStore.toggleShowWidgetType}
                                        icon="fa-light fa-chart-pie-simple"
                                        label={`${i18n.t('dashboards.createWidget')} - new`}
                                    />
                                )
                                : null }
                            {process.env.USE_NEW_DASHBOARD_ENDPOINT
                                ? (
                                    <Button
                                        cssClass="large tertiary"
                                        onClick={this.DynamicDashboardStore.toggleUseNewDashboardData}
                                        icon="fa-light fa-chart-pie-simple"
                                        label={`toggle new dashboard - ${this.DynamicDashboardStore.useNewReport}`}
                                    />
                                ) : null }
                            {process.env.USE_NEW_DASHBOARD_ENDPOINT && this.DynamicDashboardStore.oId === 6
                                ? (
                                    <Button
                                        cssClass="large tertiary"
                                        onClick={this.DynamicDashboardStore.toggleShowFilters}
                                        icon="fa-light fa-chart-pie-simple"
                                        label={i18n.t('dynamicDash.setFilters')}
                                    />
                                ) : null }
                            {this.renderMenu()}
                        </Fragment>
                    ) : null }
                </div>
            </Fragment>
        )
    }

    getChartTitle = (chart) => chart.title

    render() {
        const { history, surveyDash } = this.props
        const { initializing } = this.state
        if (initializing) return <AppLoading noBg />
        const {
            layouts,
            editing,
            defaultGridProps,
            responsiveCols,
            gapSize,
            widgets,
            loading,
            showManageDashboardsModal,
            toggleManageDashboardsModal,
            confirmationModal,
        } = this.DynamicDashboardStore

        const {
            show, onTrue, onFalse, onClose, title, description,
        } = confirmationModal

        const layoutClone = Object.keys(layouts).reduce((newLayout, l) => (
            { ...newLayout, [l]: layouts[l].slice() }
        ), {})

        if (loading) {
            return (
                <div>
                    <div>
                        <DashboardHeader
                            DynamicDashboardStore={this.DynamicDashboardStore}
                            dashboardWizardStore={this.dashboardWizardStore}
                            history={history}
                            surveyDash={surveyDash}
                        />
                    </div>
                    <div
                        style={{
                            display: 'flex', alignItems: 'center', flexDirection: 'column', minHeight: 'calc(100vh - 350px)',
                        }}
                        className="grid-parent"
                    >
                        <div className="levelHeader" style={{ position: 'relative' }}>
                            {this.renderLevelSelect()}
                            {this.renderLevelHeaderButtons()}
                        </div>
                        <div className="menuOptionsHeader">
                            {this.renderDashboardMenuOptions()}
                        </div>
                        <div style={{ width: '100%' }} className="grid-Container">
                            <div style={{
                                height: '200px',
                                position: 'relative',
                            }}
                            >
                                <AppLoading noBg showTypewriter />
                            </div>
                        </div>
                    </div>
                </div>
            )
        }

        return (
            <Fragment>
                <div id="dynamicDashboard">
                    <div>
                        <DashboardHeader
                            DynamicDashboardStore={this.DynamicDashboardStore}
                            dashboardWizardStore={this.dashboardWizardStore}
                            history={history}
                            surveyDash={surveyDash}
                        />
                    </div>
                    {widgets && widgets.length
                        ? (
                            <div
                                style={{
                                    display: 'flex', alignItems: 'center', flexDirection: 'column',
                                }}
                                className="grid-parent"
                            >
                                <div className="levelHeader" style={{ position: 'relative' }}>
                                    {this.renderLevelSelect()}
                                    {this.renderLevelHeaderButtons()}
                                </div>
                                <div className="menuOptionsHeader">
                                    {this.renderDashboardMenuOptions()}
                                </div>
                                <div className="pinnedFilter" style={{ paddingLeft: '24px', width: '100%' }}>
                                    {this.pinnedFilter()}
                                </div>
                                <div style={{ width: '100%' }} className="grid-Container" id="dashboardContainer">
                                    {!this.dashboardWizardStore.show && !this.dashboardWizardStore.showWidgetCreator
                                        ? (
                                            <ResponsiveGridLayout
                                                theStore={this.DynamicDashboardStore}
                                                className="layout"
                                                layouts={layoutClone}
                                                breakpoints={{
                                                    lg: 1400, md: 1109, sm: 768, xs: 480, xxs: 380,
                                                }}
                                                margin={[gapSize, gapSize]}
                                                cols={responsiveCols}
                                                {...defaultGridProps}
                                                onLayoutChange={this.DynamicDashboardStore.onLayoutChange}
                                                isDraggable={editing}
                                                isResizable={editing}
                                            >
                                                {this.renderElements()}
                                            </ResponsiveGridLayout>
                                        )
                                        : null }
                                </div>
                            </div>
                        )
                        : (
                            <div
                                style={{
                                    display: 'flex', alignItems: 'center', flexDirection: 'column',
                                }}
                                className="grid-parent"
                            >
                                <div className="levelHeader" style={{ position: 'relative' }}>
                                    {this.renderLevelSelect()}
                                    {this.renderLevelHeaderButtons()}
                                </div>
                                <div className="menuOptionsHeader">
                                    {this.renderDashboardMenuOptions()}
                                </div>
                                <NoDashboardsView
                                    dashStore={this.DynamicDashboardStore}
                                    showCreateWidget={this.dashboardWizardStore.toggleShow}
                                />
                            </div>
                        )}
                    <CreationWizard dashboardWizardStore={this.dashboardWizardStore} />
                    <TemplateCloneModal
                        show={this.DynamicDashboardStore.showTemplateCloneModal}
                        dashStore={this.DynamicDashboardStore}
                        onClose={this.DynamicDashboardStore.hideTemplateCloneModal}
                    />
                    <DashboardClone
                        show={this.DynamicDashboardStore.showDashboardClone}
                        dashStore={this.DynamicDashboardStore}
                        onClose={() => { this.DynamicDashboardStore.showDashboardClone = false }}
                        key={uuidv4()}
                    />
                    <DashboardRename
                        show={this.DynamicDashboardStore.showDashboardRename}
                        dashStore={this.DynamicDashboardStore}
                        onClose={() => { this.DynamicDashboardStore.showDashboardRename = false }}
                        key={uuidv4()}
                    />
                    <DashboardDelete
                        show={this.DynamicDashboardStore.showDashboardDelete}
                        dashStore={this.DynamicDashboardStore}
                        onClose={() => { this.DynamicDashboardStore.showDashboardDelete = false }}
                    />
                    <ComparisonModal
                        dashStore={this.DynamicDashboardStore}
                        showComparisonModal={this.state.showComparisonModal}
                        toggleComparisonModal={this.toggleComparisonModal}
                    />
                    <FilterModal
                        show={this.DynamicDashboardStore.showFilters}
                        reportStore={this.DynamicDashboardStore.filterStore}
                        filters={this.DynamicDashboardStore.filters}
                        onSave={this.DynamicDashboardStore.saveFilter}
                        onClose={() => { this.DynamicDashboardStore.showFilters = false }}
                    />
                </div>
                <DashboardExport
                    show={this.DynamicDashboardStore.showDashboardExport}
                    dashStore={this.DynamicDashboardStore}
                    onClose={() => { this.DynamicDashboardStore.showDashboardExport = false }}
                    key={uuidv4()}
                />
                {showManageDashboardsModal
                    ? (
                        <ManageDashboardsModal
                            dashStore={this.DynamicDashboardStore}
                            showManageDashboardsModal
                            onClose={toggleManageDashboardsModal}
                        />
                    )
                    : null }
                <ConfirmationModal
                    show={show}
                    title={title}
                    description={description}
                    onClose={onClose}
                    onTrue={onTrue}
                    onFalse={onFalse}
                />
                {process.env.NEW_WIDGET_CREATOR ? (
                    <>
                        <WidgetType dashboardWizardStore={this.dashboardWizardStore} />
                        <WidgetCreator dashboardWizardStore={this.dashboardWizardStore} />
                    </>
                ) : null }
                <FilterRespondentTypeModal
                    dashStore={this.DynamicDashboardStore}
                />
            </Fragment>
        )
    }
}

export default DynamicDashboard
